import { Button, FileButton, Group, SegmentedControl, Stack, Text } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';

import Drawer from '../drawer/drawer';

import { ImportFieldsFormat } from '@data-access';

interface ImportConnectivityFormValues {
  data_file: File | null;
  fields_file: File | null;
  format: ImportFieldsFormat;
}

interface TransformedValues {
  data_file: File;
  fields_file: File;
  format: ImportFieldsFormat;
}

type TransformValues = (
  values: ImportConnectivityFormValues,
) => TransformedValues;

interface ImportConnectivityFormProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (values: TransformedValues) => void;
  loading?: boolean;
  submitLabel?: string;
}

export function ImportConnectivityForm({
  opened,
  onClose,
  onSubmit,
  loading,
  submitLabel,
}: ImportConnectivityFormProps) {
  const { t } = useTranslation();
  const form = useForm<ImportConnectivityFormValues, TransformValues>({
    initialValues: {
      data_file: null,
      fields_file: null,
      format: 'xml',
    },
    validate: {
      data_file: isNotEmpty(t('form.required')),
      fields_file: isNotEmpty(t('form.required')),
    },
    transformValues: (values) => ({
      data_file: values.data_file!,
      fields_file: values.fields_file!,
      format: values.format,
    }),
  });

  const fieldsFileAccept =
    form.values.format === 'shape'
      ? '.zip,.shp,.dbf,.shx'
      : '.zip,.rar,.7z,.gz,.xml';

  const fieldsFileLabel =
    form.values.format === 'shape'
      ? t('connectivity.fields-file-shape')
      : t('connectivity.fields-file-xml');

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = (values: TransformedValues) => {
    onSubmit(values);
    form.reset();
    onClose();
  };

  return (
    <Drawer
      onClose={handleClose}
      opened={opened}
      position="right"
      size="auto"
      title={t('connectivity.add-connectivity-title')}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md" mt="sm">
          <div>
            <Text fw={500} mb="xs" size="sm">
              {t('connectivity.format')}
            </Text>
            <SegmentedControl
              data={[
                { label: t('connectivity.format-xml'), value: 'xml' },
                { label: t('connectivity.format-shape'), value: 'shape' },
              ]}
              fullWidth
              onChange={(value) => {
                form.setFieldValue('format', value as ImportFieldsFormat);
                form.setFieldValue('fields_file', null);
              }}
              value={form.values.format}
            />
          </div>

          <div>
            <FileButton
              accept=".xlsx,.xls"
              onChange={(file) => {
                form.setFieldValue('data_file', file);
              }}
            >
              {(props) => (
                <Button {...props} fullWidth>
                  {t('common.selectionner-un-fichier')} - {t('connectivity.data-file')}
                </Button>
              )}
            </FileButton>
            {form.getValues().data_file ? (
              <Text mt="xs" size="sm">
                {t('field.form.fileSelected')} {form.getValues().data_file?.name}
              </Text>
            ) : null}
            {form.errors.data_file && (
              <Text c="red" mt="xs" size="sm">
                {form.errors.data_file}
              </Text>
            )}
          </div>

          <div>
            <FileButton
              accept={fieldsFileAccept}
              onChange={(file) => {
                form.setFieldValue('fields_file', file);
              }}
            >
              {(props) => (
                <Button {...props} fullWidth>
                  {t('common.selectionner-un-fichier')} - {fieldsFileLabel}
                </Button>
              )}
            </FileButton>
            {form.getValues().fields_file ? (
              <Text mt="xs" size="sm">
                {t('field.form.fileSelected')} {form.getValues().fields_file?.name}
              </Text>
            ) : null}
            {form.errors.fields_file && (
              <Text c="red" mt="xs" size="sm">
                {form.errors.fields_file}
              </Text>
            )}
          </div>
        </Stack>

        <Group mt="xl">
          <Button loading={loading} type="submit">
            {submitLabel ?? t('connectivity.import')}
          </Button>
          <Button color="red" onClick={handleClose} type="button">
            {t('common.cancel')}
          </Button>
        </Group>
      </form>
    </Drawer>
  );
}

export default ImportConnectivityForm;
